import React from 'react'
import styled from 'styled-components'
import { graphql } from 'gatsby'
import useScreenOrientation from '../hooks/useScreenOrientation'
import pxToRem from '../utils/pxToRem'
import SideNavigation, { SideNavTypeEnum } from '../components/SideNavigation/SideNavigation'
import Hero, { HeroVariantEnum } from '../components/Hero/Hero'
import Layout from '../components/Layout/Layout'
import Seo from '../components/Seo/Seo'

import { deviceMax } from '../theme/breakpoints'
import { MainContainer } from '../theme/mainContainer.styled'
import NotFoundPage from '../pages/404'
import BlockFinancialResult from '../components/BlockFinancialResult/BlockFinancialResult'
import BlockWithDocuments from '../components/BlockWithDocuments/BlockWithDocuments'
import useMediaQuery from '../hooks/useMediaQuery'
import CardContact from '../components/CardContact/CardContact'
import RoutingEnum from '../routes/appRoutes'
import { SUSTAINABILITY_REPORTS_NAME } from '../utils/gtmEvents'

const PORTRAIT_PRIMARY = 0

const StyledMainLayout = styled(MainContainer)`
  justify-content: center;
  display: flex;
  padding: ${pxToRem(10)} 0;
  @media ${deviceMax.tablet} {
    margin: ${pxToRem(20)} ${pxToRem(20)};
    flex-direction: column;
    margin: 0;
    max-width: 100%;
  }
`

const ContactsCardsContainerDesktop = styled.div`
  display: block;
  @media ${deviceMax.mobileL} {
    display: none;
  }
`

const ContactsCardsContainerMobile = styled.div`
  display: none;
  @media ${deviceMax.mobileL} {
    justify-content: center;
    display: flex;
  }
`

const StyledLeftSide = styled.div`
  flex-direction: column;
  display: flex;
  width: 100%;
  max-width: ${pxToRem(384)};
  margin-right: ${pxToRem(20)};
  gap: ${pxToRem(20)};
`

const StyledRightSide = styled.div`
  position: relative;
  flex-direction: column;
  display: flex;
  width: 100%;
  margin: auto;
  max-width: ${pxToRem(792)};
  gap: ${pxToRem(20)};
`

const StyledCardContact = styled(CardContact)`
  margin-bottom: ${pxToRem(20)};
  @media ${deviceMax.mobileL} {
    margin: 0 ${pxToRem(10)};
  }
`

const StyledBlockPresentation = styled.div`
  padding: ${pxToRem(20)};
  border-radius: ${pxToRem(5)};
  background-color: ${({ theme }) => theme.colors.unique.white};
`
const StyledBlockFinancialResult = styled(BlockFinancialResult)`
  margin-bottom: 0;
`

const contentBlocEnum = Object.freeze({
  blocDocument: 'contentBlocWithDocuments',
  blocFinancial: 'componentFinancialResultsBloc',
})

const Governance = ({ data, location }) => {
  if (data.contentfulTemplatePageGovernance === null) {
    return <NotFoundPage location={location} />
  }
  const {
    backgroundColor,
    blocsContent,
    imageHeaderDesktop,
    imageHeaderMobile,
    pageTitle,
    seoMetaTitle,
    seoMetaDescription,
    investorCheckbox,
    slug,
  } = data.contentfulTemplatePageGovernance

  const { contact } = data.contentfulPageInvestors

  const parentPageNameForGtm =
    RoutingEnum.policiesReportsAndCertifications === location.pathname &&
    SUSTAINABILITY_REPORTS_NAME

  const isMobile = useMediaQuery(deviceMax.mobileL)
  const isTablet = useMediaQuery(deviceMax.tablet)
  const screenOrientation = useScreenOrientation()

  const showSideNavigation = !(
    isMobile ||
    (isTablet && screenOrientation === PORTRAIT_PRIMARY) ||
    !investorCheckbox
  )

  const blocs = blocsContent.map((bloc, index) => {
    if (bloc.sys.contentType.sys.id === contentBlocEnum.blocDocument) {
      return (
        <StyledBlockPresentation>
          <BlockWithDocuments
            documents={bloc.blocDocuments}
            parentPageNameForGtm={parentPageNameForGtm}
            text={bloc.textBloc}
            title={bloc.title}
          />
        </StyledBlockPresentation>
      )
    }
    if (bloc.sys.contentType.sys.id === contentBlocEnum.blocFinancial) {
      return (
        <StyledBlockFinancialResult
          key={index}
          block={bloc}
          parentPageNameForGtm={parentPageNameForGtm}
        />
      )
    }
  })

  const contactsCards =
    contact &&
    contact.map((contact) => {
      return (
        <StyledCardContact
          department={contact.department}
          email={contact.email}
          firstname={contact.firstname}
          key={contact.id}
          lastname={contact.lastname}
          phone={contact.phone}
        />
      )
    })

  return (
    <Layout>
      <Seo
        title={seoMetaTitle ? seoMetaTitle.seoMetaTitle : pageTitle}
        description={seoMetaDescription?.seoMetaDescription}
        canonicalHref={location.pathname}
      />
      <Hero
        mediaDesktop={imageHeaderDesktop}
        mediaMobile={imageHeaderMobile}
        title={pageTitle}
        backgroundColor={backgroundColor}
        pathname={location.pathname}
        variant={
          investorCheckbox ? HeroVariantEnum.investor : HeroVariantEnum.regular
        }
      />
      <StyledMainLayout>
        {investorCheckbox && (
          <StyledLeftSide>
            {showSideNavigation && (
              <SideNavigation variant={SideNavTypeEnum.investor} />
            )}
            {contact && (
              <ContactsCardsContainerDesktop>
                {contactsCards}
              </ContactsCardsContainerDesktop>
            )}
          </StyledLeftSide>
        )}

        <StyledRightSide $isInvestor={investorCheckbox}>
          {contact && investorCheckbox && (
            <ContactsCardsContainerMobile>
              {contactsCards}
            </ContactsCardsContainerMobile>
          )}
          {blocs}
        </StyledRightSide>
      </StyledMainLayout>
    </Layout>
  )
}

export default Governance

export const governance = graphql`
  query GovernanceQuery($contentful_id: String!) {
    contentfulPageInvestors {
      contact {
          ...contactFragment
      }
    }
    contentfulTemplatePageGovernance(contentful_id: { eq: $contentful_id }) {
      slug
      investorCheckbox
      pageTitle
      seoMetaDescription {
        seoMetaDescription
      }
      id
      backgroundColor
      imageHeaderMobile {
          ...imageHeaderFragment
      }
      imageHeaderDesktop {
          ...imageHeaderFragment
      }
      blocsContent {
        ... on ContentfulContentBlocWithDocuments {
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          id
          title
          textBloc {
            content
          }
          blocDocuments {
              ...documentFragment
          }
        }
        ... on ContentfulComponentFinancialResultsBloc {
          sys {
            contentType {
              sys {
                id
              }
            }
          }
          id
          blocDescription {
            blocDescription
          }
          blocTitle
          financialTabs {
            tabTitle
            contents {
              ... on ContentfulComponentFinancialDocumentsBloc {
                blocTitle
                sections {
                  blocTitle
                  documents {
                      ...documentFragment
                  }
                }
                documents {
                    ...documentFragment
                }
              }
            }
          }
        }
      }
    }
  }
`
